import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import {useAuth} from '../context/AuthContext';
import {Navigate} from 'react-router-dom';
import {formatCPF, formatDate, formatPhone, unmask} from '../utils/formatters';
import {getInitials} from '../utils/userUtils';
import {customerApi} from '../services/api';
import {AddressDto} from '../types';
import AddressManageModal from '../components/AddressManageModal';
import ConfirmModal from '../components/ConfirmModal';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 2rem auto;
  padding: 0 1rem;

  @media (max-width: 768px) {
    margin: 1rem auto;
  }
`;

const ProfileContent = styled.div`
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  padding: 2.5rem;
  margin-bottom: 2rem;

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const AddressCard = styled.div`
  background: transparent;
`;

const AddressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const AddressItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);

  &:hover {
    border-color: #e74c3c;
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(231, 76, 60, 0.1);
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem;
  }
`;

const AddressInfo = styled.div`
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
`;

const AddressIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: #fff5f5;
  color: #e74c3c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const AddressText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  strong {
    color: #2d3748;
    font-size: 1.1rem;
    font-weight: 700;
  }

  span {
    color: #718096;
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const PlaceholderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  background-color: #f8fafc;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  gap: 1rem;

  i {
    font-size: 3rem;
    color: #cbd5e0;
  }

  p {
    color: #718096;
    margin: 0;
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2.5rem;
  gap: 1.5rem;
`;

const AvatarContainer = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  font-weight: 700;
  color: #a0aec0;
  overflow: hidden;
  border: 4px solid #fff;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PhotoUploadButton = styled.label`
  position: absolute;
  bottom: 0;
  right: 0;
  background-color: #e74c3c;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(231, 76, 60, 0.3);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 3px solid #ffffff;

  &:hover {
    background-color: #c0392b;
    transform: scale(1.1) rotate(15deg);
  }

  i {
    font-size: 1.2rem;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const InfoGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #718096;
    margin-bottom: 0.25rem;
  }
  
  div, input, select {
    font-size: 1.1rem;
    color: #2d3748;
    width: 100%;
  }

  input, select {
    padding: 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background-color: #f8fafc;
    
    &:focus {
      outline: none;
      border-color: #e74c3c;
      box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'outline' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;

  ${props => props.variant === 'primary' && `
    background-color: #e74c3c;
    color: white;
    border: none;
    &:hover { background-color: #c0392b; }
    &:disabled { background-color: #feb2b2; cursor: not-allowed; }
  `}

  ${props => props.variant === 'secondary' && `
    background-color: #f8fafc;
    color: #4a5568;
    border: 1px solid #e2e8f0;
    &:hover { background-color: #edf2f7; }
  `}

  ${props => props.variant === 'outline' && `
    background-color: transparent;
    color: #e74c3c;
    border: 1px solid #e74c3c;
    &:hover { background-color: #fff5f5; }
  `}
`;

const AddressActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const DeleteButton = styled(Button)`
  padding: 0.5rem;
  min-width: auto;
  color: #e53e3e;
  border-color: #feb2b2;
  
  &:hover {
    background-color: #fff5f5;
    border-color: #e53e3e;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  color: #2d3748;
  margin-bottom: 2rem;
`;

const ProfilePage: React.FC = () => {
  const { user, isAuthenticated, updateUser } = useAuth();

  const [activeTab, setActiveTab] = useState<'profile' | 'addresses'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addresses, setAddresses] = useState<AddressDto[]>([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<AddressDto | undefined>(undefined);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchAddresses = async () => {
    try {
      setIsLoadingAddresses(true);
      const data = await customerApi.listAddresses();
      setAddresses(data);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const data = await customerApi.getProfile();
      updateUser(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAddresses();
      fetchProfile();
    }
  }, [isAuthenticated]);

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    cpf: user?.cpf || '',
    email: user?.email || '',
    phone: user?.phone || '',
    gender: user?.gender || 'MALE',
    birthDate: user?.birthDate || '',
  });

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'phone') {
      formattedValue = formatPhone(value);
    } else if (name === 'cpf') {
      formattedValue = formatCPF(value);
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }));
  };

  const handleEdit = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      cpf: formatCPF(user?.cpf || ''),
      email: user?.email || '',
      phone: formatPhone(user?.phone || ''),
      gender: user?.gender || 'MALE',
      birthDate: user?.birthDate || '',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // O CPF não é editável e não faz parte do contrato de atualização de
      // perfil no backend — enviá-lo causa 400 (campo desconhecido).
      const updatedProfile = await customerApi.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        gender: formData.gender as 'MALE' | 'FEMALE',
        birthDate: formData.birthDate,
        phone: unmask(formData.phone),
      });

      // Update AuthContext
      updateUser(updatedProfile);
      
      setIsEditing(false);
      toast.success('Perfil atualizado com sucesso!');
      
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Erro ao atualizar perfil. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 2MB.');
      return;
    }

    setIsLoading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        try {
          const contentType = file.type;
          // Extract only the base64 part
          const base64Data = base64String.split(',')[1];
          
          const response = await customerApi.updateImage({
            base64: base64Data,
            contentType
          });
          
          // Get the URL from response if provided, or use the base64 for immediate feedback
          const imageUrl = response.image || response.imageUrl || base64String;
          
          updateUser({ image: imageUrl });
          toast.success('Foto atualizada com sucesso!');
        } catch (err) {
          console.error('Error uploading photo:', err);
          toast.error('Erro ao enviar foto.');
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erro ao processar imagem.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAddress = (addressId: string | undefined) => {
    setAddressToDelete(addressId);
    setIsConfirmModalOpen(true);
  };

  const confirmDeleteAddress = async () => {
    if (!addressToDelete) {
      toast.error('Erro: ID do endereço não encontrado.');
      setIsConfirmModalOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      await customerApi.deleteAddress(addressToDelete);
      setAddresses(prev => prev.filter(addr => addr.id !== addressToDelete));
      toast.success('Endereço removido com sucesso!');
    } catch (error) {
      console.error('Error deleting address:', error);
      toast.error('Erro ao remover endereço.');
    } finally {
      setIsLoading(false);
      setIsConfirmModalOpen(false);
      setAddressToDelete(undefined);
    }
  };

  const handleAddAddress = () => {
    setEditingAddress(undefined);
    setIsAddressModalOpen(true);
  };

  const handleEditAddress = (address: AddressDto) => {
    setEditingAddress(address);
    setIsAddressModalOpen(true);
  };

  const handleSaveAddress = async (addressData: AddressDto) => {
    try {
      if (editingAddress?.id) {
        await customerApi.updateAddress(editingAddress.id, addressData);
        toast.success('Endereço atualizado com sucesso!');
      } else {
        await customerApi.addAddress(addressData);
        toast.success('Endereço adicionado com sucesso!');
      }
      fetchAddresses();
    } catch (error) {
      console.error('Error saving address:', error);
      throw error;
    }
  };

  return (
    <Layout>
      <div className="py-4" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h1 className="display-5 fw-bold mb-4">Minha Conta</h1>

        <ul className="nav nav-tabs mb-4 border-bottom-0">
          <li className="nav-item">
            <button 
              className={`nav-link border-0 fw-bold px-4 py-3 ${activeTab === 'profile' ? 'active text-primary border-bottom border-primary border-3' : 'text-muted'}`}
              onClick={() => setActiveTab('profile')}
              style={{ backgroundColor: 'transparent', borderRadius: 0 }}
            >
              <i className="fas fa-user-circle me-2"></i>
              Meus Dados
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link border-0 fw-bold px-4 py-3 ${activeTab === 'addresses' ? 'active text-primary border-bottom border-primary border-3' : 'text-muted'}`}
              onClick={() => setActiveTab('addresses')}
              style={{ backgroundColor: 'transparent', borderRadius: 0 }}
            >
              <i className="fas fa-map-marker-alt me-2"></i>
              Endereços
            </button>
          </li>
        </ul>
        
        <div className="tab-content animate-fade-in">
          {activeTab === 'profile' ? (
            <ProfileCard>
              <ProfileHeader>
                <AvatarContainer>
                  <Avatar>
                    {user?.image ? (
                      <img src={user.image} alt={user.name} />
                    ) : (
                      getInitials(user?.name || '')
                    )}
                  </Avatar>
                  <PhotoUploadButton>
                    <i className="fas fa-camera"></i>
                    <HiddenInput 
                      type="file" 
                      ref={fileInputRef} 
                      onChange={handleFileChange} 
                      accept="image/*"
                    />
                  </PhotoUploadButton>
                </AvatarContainer>
                {!isEditing && (
                  <div style={{ textAlign: 'center' }}>
                    <h2 style={{ margin: '0', color: '#2d3748' }}>{user?.name}</h2>
                    <p style={{ margin: '0.25rem 0 0', color: '#718096' }}>{user?.email}</p>
                  </div>
                )}
              </ProfileHeader>

              <form onSubmit={handleSubmit}>
                {!isEditing ? (
                  <>
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4 mb-4">
                      <div className="col">
                        <InfoGroup>
                          <label>CPF</label>
                          <div>{formatCPF(user?.cpf || '')}</div>
                        </InfoGroup>
                      </div>
                      <div className="col">
                        <InfoGroup>
                          <label>Telefone</label>
                          <div>{formatPhone(user?.phone || '')}</div>
                        </InfoGroup>
                      </div>
                      <div className="col">
                        <InfoGroup>
                          <label>Gênero</label>
                          <div>{user?.gender === 'MALE' ? 'Masculino' : 'Feminino'}</div>
                        </InfoGroup>
                      </div>
                      <div className="col">
                        <InfoGroup>
                          <label>E-mail</label>
                          <div style={{ wordBreak: 'break-all' }}>{user?.email}</div>
                        </InfoGroup>
                      </div>
                      <div className="col">
                        <InfoGroup>
                          <label>Data de Nascimento</label>
                          <div>{formatDate(user?.birthDate || '')}</div>
                        </InfoGroup>
                      </div>
                    </div>
                    <Button type="button" variant="outline" onClick={handleEdit}>
                      Editar Informações
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="row g-3">
                      <div className="col-12 col-sm-6">
                        <InfoGroup>
                          <label>Nome</label>
                          <input
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                          />
                        </InfoGroup>
                      </div>
                      <div className="col-12 col-sm-6">
                        <InfoGroup>
                          <label>Sobrenome</label>
                          <input
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            required
                          />
                        </InfoGroup>
                      </div>
                      <div className="col-12 col-sm-6">
                        <InfoGroup>
                          <label>CPF</label>
                          <input
                            name="cpf"
                            value={formData.cpf}
                            readOnly
                            disabled
                            title="O CPF não pode ser alterado"
                            maxLength={14}
                          />
                        </InfoGroup>
                      </div>
                      <div className="col-12 col-sm-6">
                        <InfoGroup>
                          <label>Telefone</label>
                          <input
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            maxLength={15}
                          />
                        </InfoGroup>
                      </div>
                      <div className="col-12 col-sm-6">
                        <InfoGroup>
                          <label>Gênero</label>
                          <select
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            required
                          >
                            <option value="MALE">Masculino</option>
                            <option value="FEMALE">Feminino</option>
                          </select>
                        </InfoGroup>
                      </div>
                      <div className="col-12 col-sm-6">
                        <InfoGroup>
                          <label>E-mail</label>
                          <input
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </InfoGroup>
                      </div>
                    </div>
                    <div className="row g-3 mt-1">
                      <div className="col-12 col-sm-6">
                        <InfoGroup>
                          <label>Data de Nascimento</label>
                          <input
                            name="birthDate"
                            type="date"
                            value={formData.birthDate}
                            onChange={handleChange}
                            required
                          />
                        </InfoGroup>
                      </div>
                    </div>
                    <ButtonGroup>
                      <Button type="button" variant="secondary" onClick={handleCancel} disabled={isLoading}>
                        Cancelar
                      </Button>
                      <Button type="submit" variant="primary" disabled={isLoading}>
                        {isLoading ? 'Salvando...' : 'Salvar Alterações'}
                      </Button>
                    </ButtonGroup>
                  </>
                )}
              </form>
            </ProfileCard>
          ) : (
            <AddressCard>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 style={{ fontSize: '1.5rem', color: '#2d3748', margin: '0' }}>Meus Endereços</h2>
                {addresses.length > 0 && (
                  <Button variant="primary" onClick={handleAddAddress} style={{ flex: 'none', padding: '0.5rem 1rem' }}>
                    Novo Endereço
                  </Button>
                )}
              </div>
              
              {isLoadingAddresses ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>Carregando endereços...</div>
              ) : addresses.length > 0 ? (
                <div className="row g-3">
                  {addresses.map((address, index) => (
                    <div className="col-12 col-lg-6" key={index}>
                      <AddressItem style={{ height: '100%' }}>
                        <AddressInfo>
                          <AddressIcon>
                            <i className={`fas ${address.nickname?.toLowerCase() === 'casa' ? 'fa-home' : address.nickname?.toLowerCase() === 'trabalho' ? 'fa-briefcase' : 'fa-map-marker-alt'}`}></i>
                          </AddressIcon>
                          <AddressText>
                            <strong>{address.nickname || address.street}</strong>
                            <span>{address.street}, {address.number}{address.complement ? ` - ${address.complement}` : ''}</span>
                            <span>{address.neighborhood}, {address.city} - {address.state}</span>
                          </AddressText>
                        </AddressInfo>
                        <AddressActions>
                          <Button variant="outline" onClick={() => handleEditAddress(address)} style={{ padding: '0.5rem', minWidth: 'auto', borderRadius: '12px' }}>
                            <i className="fas fa-edit"></i>
                          </Button>
                          <DeleteButton variant="outline" onClick={() => handleDeleteAddress(address.id)} style={{ borderRadius: '12px' }}>
                            <i className="fas fa-trash-alt"></i>
                          </DeleteButton>
                        </AddressActions>
                      </AddressItem>
                    </div>
                  ))}
                  <div className="col-12">
                    <Button variant="outline" onClick={handleAddAddress} style={{ marginTop: '1rem', padding: '1.5rem', borderStyle: 'dashed', background: '#f8fafc', borderRadius: '16px' }}>
                      <i className="fas fa-plus" style={{ marginRight: '8px' }}></i>
                      Adicionar Novo Endereço
                    </Button>
                  </div>
                </div>
              ) : (
                <PlaceholderContainer>
                  <i className="fas fa-map-marker-alt"></i>
                  <p>Você ainda não tem endereços cadastrados.</p>
                  <Button variant="primary" onClick={handleAddAddress} style={{ marginTop: '1rem' }}>
                    Adicionar Meu Primeiro Endereço
                  </Button>
                </PlaceholderContainer>
              )}
            </AddressCard>
          )}
        </div>

        {isAddressModalOpen && (
          <AddressManageModal
            address={editingAddress}
            onClose={() => setIsAddressModalOpen(false)}
            onSave={handleSaveAddress}
          />
        )}

        <ConfirmModal
          isOpen={isConfirmModalOpen}
          title="Excluir Endereço"
          message="Tem certeza que deseja remover este endereço? Esta ação não pode ser desfeita."
          onConfirm={confirmDeleteAddress}
          onCancel={() => setIsConfirmModalOpen(false)}
          confirmLabel="Excluir"
          cancelLabel="Cancelar"
          isLoading={isLoading}
        />
      </div>
    </Layout>
  );
};

export default ProfilePage;